import React, { useEffect, useState } from "react";
import {
  Box,
  CssBaseline,
  Paper,
  Typography,
  Button,
  TextField,
} from "@mui/material";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import AppBarComponent from "../../components/layout/AppbarComponent";
// import axios from "axios";
import api from "../../api/axiosInstance"
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_URL = process.env.REACT_APP_API_URL;

// ---------- Config ----------
const processRatios = [
  { key: "plan_ratio", label: "การวางแผน (Planning)" },
  { key: "req_ratio", label: "การวิเคราะห์ความต้องการ (Requirement Analysis)" },
  { key: "design_ratio", label: "การออกแบบซอฟต์แวร์ (Design)" },
  { key: "dev_ratio", label: "การพัฒนาซอฟต์แวร์ (Development)" },
  { key: "test_ratio", label: "การทดสอบซอฟต์แวร์ (Testing)" },
  { key: "deliver_ratio", label: "การส่งมอบ (Delivery)" },
];

const extraRatios = [
  { key: "risk_ratio", label: "ความเสี่ยง (Risk Management)" },
  {
    key: "tech_ratio",
    label: "เทคโนโลยีที่คิดค้นขึ้นใหม่ (Technology Management)",
  },
  { key: "other_ratio", label: "ปัจจัยอื่นๆ (Other)" },
];

const mainSalaries = [
  { key: "pm_salary", label: "ผู้บริหารโครงการ" },
  { key: "sa_salary", label: "นักวิเคราะห์และออกแบบระบบงาน" },
  { key: "dev_salary", label: "นักพัฒนาซอฟต์แวร์" },
  { key: "tester_salary", label: "นักทดสอบซอฟต์แวร์" },
  { key: "workteam_salary", label: "ฝ่ายปฏิบัติการสนับสนุน" },
];

const supportSalaries = [{ key: "support_salary", label: "ผู้เกี่ยวข้อง" }];

// ---------- Reusable Row ----------
const EditableRow = ({ item, parameter, newValues, onChange }) => {
  const value = newValues[item.key] ?? "";
  const isInvalid = value !== "" && Number(value) < 0;

  return (
    <TableRow key={item.key}>
      <TableCell>{item.label}</TableCell>
      <TableCell>{parameter ? parameter[item.key] : "-"}</TableCell>
      <TableCell>
        <TextField
          name={item.key}
          type="number"
          slotProps={{ input: { min: 0 } }}
          value={value}
          onChange={onChange}
          error={isInvalid}
          helperText={isInvalid ? "ค่าต้องไม่เป็นค่าติดลบ" : ""}
          fullWidth
        />
      </TableCell>
    </TableRow>
  );
};

const SetupParameter = () => {
  const [parameter, setParameter] = useState(null);
  console.log("Current Parameter:", parameter);
  const [loading, setLoading] = useState(false);
  const [newValues, setNewValues] = useState({});
  console.log("New Values:", newValues);

  // For file upload
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [referenceUrl, setReferenceUrl] = useState(null);

  const fetchParameter = async () => {
    setLoading(true);
    try {
      const res = await api.get(`api/parameter/latest`);
      setParameter(res.data);
      if (res.data?.ref_filepath) {
        setReferenceUrl(`${API_URL}/parameter_info/uploads/reference/${res.data?.ref_filepath}`);
      }
    } catch (err) {
      toast.error("โหลดข้อมูลพารามิเตอร์ไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParameter();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const numericValue = Number(value);

    // enforce non-negative
    if (numericValue < 0) {
      setNewValues({ ...newValues, [name]: 0 });
    } else {
      setNewValues({ ...newValues, [name]: value });
    }
  };

  const handleSave = async () => {
    if (!parameter) return;

    // final validation: no negative numbers
    const hasNegative = Object.values(newValues).some((val) => Number(val) < 0);
    if (hasNegative) {
      toast.error("ไม่สามารถบันทึกได้: พบค่าติดลบ");
      return;
    }

    // final validation: ratio sum must be 100
    const ratioSum = processRatios.reduce(
      (sum, r) => sum + (Number(newValues[r.key]) || 0),
      0
    );
    if (ratioSum !== 100 && ratioSum !== 0) {
      toast.error("ไม่สามารถบันทึกได้: ผลรวมของสัดส่วนค่าความพยายามต้องเท่ากับ 100");
      return;
    }

    try {
      const response = await api.put(
        `api/parameter/editparameter/${parameter.parameter_id}`,
        newValues
      );
      toast.success(response.data?.message || "อัปเดตข้อมูลเรียบร้อยแล้ว");
      fetchParameter();
    } catch (err) {
      toast.error(err.response?.data?.message || "เกิดข้อผิดพลาดในการบันทึก");
    }
  };

  // -------- File Upload Handlers --------
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("กรุณาเลือกไฟล์ก่อนอัปโหลด");
      return;
    }
    if (!parameter) {
      toast.error("ไม่พบพารามิเตอร์สำหรับอัปโหลดไฟล์");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    try {
      setUploading(true);
      console.log("Uploading to:", `${API_URL}/parameter/upload/${parameter.parameter_id}`);
      
      const res = await api.put(`api/parameter/upload/${parameter.parameter_id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data?.message || "อัปโหลดเอกสารสำเร็จ");
      // update link immediately
      console.log("Upload response data:", res.data);
      
      if (res.data?.data?.ref_filepath) {
        setReferenceUrl(`${API_URL}/parameter_info/uploads/reference/${res.data.data.ref_filepath}`);
      }

      setFile(null);
    } catch (err) {
      toast.error(err.response?.data?.message || "อัปโหลดเอกสารล้มเหลว");
    } finally {
      setUploading(false);
    }
  };

  // -------- Validation: Ratio sum must be 100 --------
  const ratioSum = processRatios.reduce(
    (sum, r) => sum + (Number(newValues[r.key]) || 0),
    0
  );

  return (
    <>
      <CssBaseline />
      <AppBarComponent />

      <Box>
        <Typography variant="h4" align="center" gutterBottom>
          กำหนดค่าเริ่มต้น
        </Typography>

        {/* Main Ratios */}
        <TableContainer
          component={Paper}
          sx={{ maxWidth: 800, m: "auto", mb: 4 }}
        >
          <Typography variant="h5" gutterBottom>
            สัดส่วนค่าความพยายามกระบวนการทำงานหลัก
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>กระบวนการทำงาน</TableCell>
                <TableCell>อัตราส่วน</TableCell>
                <TableCell>อัตราส่วนที่แก้ไข</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {processRatios.map((item) => (
                <EditableRow
                  key={item.key}
                  item={item}
                  parameter={parameter}
                  newValues={newValues}
                  onChange={handleChange}
                />
              ))}
              <TableRow>
                <TableCell>รวม (Total)</TableCell>
                <TableCell>
                  {parameter
                    ? processRatios
                        .reduce(
                          (sum, r) => sum + (Number(parameter[r.key]) || 0),
                          0
                        )
                        .toFixed(2)
                    : "-"}
                </TableCell>
                <TableCell>
                  {ratioSum.toFixed(2)}
                  {ratioSum !== 100 && (
                    <Typography variant="body2" color="error" sx={{ ml: 1 }}>
                      (ผลรวมต้องเท่ากับ 100)
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Extra Ratios */}
        <TableContainer
          component={Paper}
          sx={{ maxWidth: 800, m: "auto", mb: 4 }}
        >
          <Typography variant="h5" gutterBottom>
            สัดส่วนค่าความพยายามกระบวนการทำงานเพิ่มเติม
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>กระบวนการทำงาน</TableCell>
                <TableCell>อัตราส่วน (ไม่เกิน)</TableCell>
                <TableCell>อัตราส่วนที่แก้ไข</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {extraRatios.map((item) => (
                <EditableRow
                  key={item.key}
                  item={item}
                  parameter={parameter}
                  newValues={newValues}
                  onChange={handleChange}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Main Salaries */}
        <TableContainer
          component={Paper}
          sx={{ maxWidth: 800, m: "auto", mb: 4 }}
        >
          <Typography variant="h5" gutterBottom>
            อัตราค่าจ้างบุคลากรหลัก (บาท/เดือน)
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>บทบาทหน้าที่หลัก</TableCell>
                <TableCell>อัตราค่าจ้าง</TableCell>
                <TableCell>อัตราค่าจ้างที่แก้ไข</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mainSalaries.map((item) => (
                <EditableRow
                  key={item.key}
                  item={item}
                  parameter={parameter}
                  newValues={newValues}
                  onChange={handleChange}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Support Salaries */}
        <TableContainer
          component={Paper}
          sx={{ maxWidth: 800, m: "auto", mb: 4 }}
        >
          <Typography variant="h5" gutterBottom>
            อัตราค่าจ้างบุคลากรสนับสนุน (บาท/เดือน)
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>บทบาทหน้าที่หลัก</TableCell>
                <TableCell>อัตราค่าจ้าง</TableCell>
                <TableCell>อัตราค่าจ้างที่แก้ไข</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {supportSalaries.map((item) => (
                <EditableRow
                  key={item.key}
                  item={item}
                  parameter={parameter}
                  newValues={newValues}
                  onChange={handleChange}
                />
              ))}
            </TableBody>
          </Table>
          {/* Reference Document Section */}
          <Box sx={{ p: 2 }}>
            {referenceUrl ? (
              <Typography variant="body2" sx={{ mb: 1 }}>
                เอกสารอ้างอิง:{" "}
                <a
                  href={referenceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "blue", textDecoration: "underline" }}
                >
                  เปิดไฟล์
                </a>
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ mb: 1 }}>
                ยังไม่มีเอกสารอ้างอิง
              </Typography>
            )}

            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileChange}
              style={{ marginBottom: "10px" }}
            />
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={uploading}
            >
              {uploading ? "กำลังอัปโหลด..." : "Upload เอกสารอ้างอิง"}
            </Button>
          </Box>
        </TableContainer>

        {/* Actions */}
        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Button
            variant="outlined"
            sx={{ mr: 2 }}
            onClick={() => setNewValues({})}
          >
            ยกเลิก
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={loading}>
            {loading ? "กำลังบันทึก..." : "บันทึก"}
          </Button>
        </Box>
      </Box>

      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default SetupParameter;
